// SubcontractorsPage.js
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { ConfigProvider, Card, Typography, Table, Form, Select, Space, Button, Input, InputNumber, Modal, Tooltip, message } from 'antd';
import { EditOutlined, DeleteOutlined, CheckOutlined, WarningOutlined, SyncOutlined, SearchOutlined, LoadingOutlined, ApiOutlined } from '@ant-design/icons';
import { US, CA } from 'country-flag-icons/react/3x2'
import {
  fetchSubcontractors,
  updateSubcontractor,
  fetchRoutes,
  fetchSubcontractorRoutes,
  updateSubcontractorRoutes,
  addRouteSubcontractor,
  removeRouteSubcontractor,
  syncYetiSubcontractors,
  fetchPrecoroSuppliers,
} from '../api';
import { useConstants } from '../context/ConstantsContext';
import SideMenu from '../components/SideMenu';
import { renderSubcontractorsSummary } from '../components/YetiSync';
import PrecoroSuppliers from '../components/PrecoroSuppliers';

const SubcontractorsPage = () => {
  const [constantsLoading, setConstantsLoading] = useState(true);
  const constants = useConstants();
  useEffect(() => {
    if (constants) {
      setConstantsLoading(false);
    }
  }, [constants]);

  const [loading, setLoading] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [filteredSubcontractors, setFilteredSubcontractors] = useState([]);
  const [routes, setRoutes] = useState([]);

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersOpen, setSuppliersOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [routeSearchQuery, setRouteSearchQuery] = useState('');
  const [expandedSubcontractor, setExpandedSubcontractor] = useState(null);
  const [selectedRouteForAssignment, setSelectedRouteForAssignment] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editingSubcontractor, setEditingSubcontractor] = useState(null);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [editForm] = Form.useForm();

  const [editRouteOpen, setEditRouteOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  const [messageApi, contextHolder] = message.useMessage();

  const loadSubcontractors = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSubcontractors();
      setSubcontractors(data);
    } catch (err) {
      console.error('Failed to fetch subcontractors:', err);
      messageApi.error('Failed to fetch subcontractors');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPrecoroSuppliers();
      setSuppliers(data);
    } catch (err) {
      console.error('Failed to fetch Precoro suppliers:', err);
      messageApi.error('Failed to fetch Precoro suppliers');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRoutes();
      setRoutes(data);
    } catch (err) {
      console.error('Failed to fetch routes:', err);
      messageApi.error('Failed to fetch routes');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadRoutes();
    loadSubcontractors();
    loadSuppliers();
  }, [loadRoutes, loadSubcontractors, loadSuppliers]);

  useEffect(() => {
    const filteredData = subcontractors.filter(subcontractor => {
      const name = subcontractor.name || 'N/A';
  
      return (
        name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  
    setFilteredSubcontractors(filteredData);
  }, [searchQuery, subcontractors]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSyncClose = () => {
    setSyncSummary(null);
    setSyncOpen(false);
  };

  const handleYetiSync = async () => {
    setSyncLoading(true);
    try {
      const response = await syncYetiSubcontractors();
      setSyncSummary(response);
    } catch (error) {
      console.error("Failed to sync Subcontractors with Yeti:", error);
      messageApi.error("Failed to sync Subcontractors with Yeti");
    } finally {
      setSyncLoading(false);
      // The sync saves as it goes, so reload even if it failed partway
      loadSubcontractors();
    }
  };

  const handleEditClickOpen = (subcontractor) => {
    setEditingSubcontractor(subcontractor);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  useEffect(() => {
    if (editOpen && editingSubcontractor) {
      editForm.setFieldsValue(editingSubcontractor);
      const currentCountry = editingSubcontractor.country;
      setFilteredSuppliers(
        suppliers.filter(supplier => supplier.country === currentCountry)
      );
    }
  }, [editOpen, editForm, editingSubcontractor, suppliers]);

  useEffect(() => {
    const currentCountry = editForm.getFieldValue('country');
    setFilteredSuppliers(
      suppliers.filter(supplier => supplier.country === currentCountry)
    );
  }, [suppliers, editForm]);

  const handleEditOk = async () => {
    try {
      let values = await editForm.validateFields();
      values = {
        ...values,
        precoro_supplier_id: values.precoro_supplier_id ?? null,  // Ensure precoro_supplier_id is included even if cleared
      };
      await updateSubcontractor(editingSubcontractor.subcontractor_id, values);
      setEditOpen(false);
      await loadSubcontractors();
      messageApi.success('Subcontractor updated successfully');
    } catch (error) {
      console.error('Failed to update subcontractor:', error);
      messageApi.error('Failed to update subcontractor');
    }
  };

  const handleExpandSubcontractor = async (subcontractorId) => {
    if (expandedSubcontractor === subcontractorId) {
      setExpandedSubcontractor(null);
    } else {
      try {
        const assignedRoutes = await fetchSubcontractorRoutes(subcontractorId);
        const assignedRouteIds = assignedRoutes.map(route => route.route_id);

        const availableRoutesForSubcontractor = routes.filter(route => !assignedRouteIds.includes(route.route_id));

        const updatedSubcontractors = subcontractors.map(sub => 
          sub.subcontractor_id === subcontractorId
            ? { ...sub, routes: assignedRoutes, availableRoutes: availableRoutesForSubcontractor }
            : sub
        );

        setSubcontractors(updatedSubcontractors);
        setExpandedSubcontractor(subcontractorId);
        setRouteSearchQuery('');
        setSelectedRouteForAssignment(null);
      } catch (error) {
        console.error('Error fetching subcontractor routes:', error);
        messageApi.error('Failed to fetch subcontractor routes');
      }
    }
  };

const handleAddSubcontractorRoute = async (subcontractorId, routeId) => {
  try {
    await addRouteSubcontractor(routeId, subcontractorId);

    const updatedRoutes = await fetchSubcontractorRoutes(subcontractorId);

    setSubcontractors(prevSubcontractors =>
      prevSubcontractors.map(sub =>
        sub.subcontractor_id === subcontractorId
          ? { ...sub, routes: updatedRoutes }
          : sub
      )
    );

    const assignedRouteIds = updatedRoutes.map(route => route.route_id);
    const availableRoutesForSubcontractor = routes.filter(route => !assignedRouteIds.includes(route.route_id));

    setSubcontractors(prevSubcontractors =>
      prevSubcontractors.map(sub =>
        sub.subcontractor_id === subcontractorId
          ? { ...sub, availableRoutes: availableRoutesForSubcontractor }
          : sub
      )
    );

    setSelectedRouteForAssignment(null);

    setExpandedSubcontractor(subcontractorId);

    messageApi.success('Route assigned successfully');
  } catch (error) {
    console.error('Failed to assign route:', error);
    messageApi.error('Failed to assign route');
  }
};

  const handleRemoveSubcontractorRoute = async (routeId, subcontractorId) => {
    console.log(`removing route subcontractor ${routeId},${subcontractorId}`)
    try {
      await removeRouteSubcontractor(routeId, subcontractorId);

      const removedRoute = routes.find(route => route.route_id === routeId);

      setSubcontractors(prevSubcontractors =>
        prevSubcontractors.map(sub =>
          sub.subcontractor_id === subcontractorId
            ? {
                ...sub,
                routes: sub.routes.filter(route => route.route_id !== routeId),
                availableRoutes: [...sub.availableRoutes, removedRoute]
              }
            : sub
        )
      );

      messageApi.success('Route assignment removed successfully');
    } catch (error) {
      console.error('Failed to remove route assignment:', error);
      messageApi.error('Failed to remove route assignment');
    }
  };

  const handleEditRouteClose = () => {
    setEditRouteOpen(false);
    setEditingRoute(null);
  };

  const handleEditRouteOpen = (subcontractorId, route) => {
    setEditingRoute({ 
      ...route, 
      subcontractorId,
      salting_amount: route.salting_amount || 0,
      number_of_saltings_included: route.number_of_saltings_included || 0,
      number_of_saltings_performed: route.number_of_saltings_performed || 0
    });
    setEditRouteOpen(true);
  };

  const handleEditRouteChange = (e) => {
    const name = e.target ? e.target.name : 'contract_type';
    const value = e.target ? e.target.value : e;
  
    setEditingRoute(prev => ({ ...prev, [name]: value }));
  };

  const handleEditRouteSave = async () => {
    try {
      await updateSubcontractorRoutes(editingRoute.subcontractorId, {
        routes: [{
          route_id: editingRoute.route_id,
          contract_type: editingRoute.contract_type,
          salting_amount: parseInt(editingRoute.salting_amount),
          number_of_saltings_included: parseInt(editingRoute.number_of_saltings_included),
          number_of_saltings_performed: parseInt(editingRoute.number_of_saltings_performed)
        }]
      });

      const updatedRoutes = await fetchSubcontractorRoutes(editingRoute.subcontractorId);

      setSubcontractors(prevSubcontractors =>
        prevSubcontractors.map(sub =>
          sub.subcontractor_id === editingRoute.subcontractorId
            ? { ...sub, routes: updatedRoutes }
            : sub
        )
      );

      handleEditRouteClose();
      setExpandedSubcontractor(editingRoute.subcontractorId);
      messageApi.success('Route assignment updated successfully');
    } catch (error) {
      console.error('Failed to update route assignment:', error);
      messageApi.error('Failed to update route assignment');
    }
  };

  const onSupplierUpdate = async (subcontractorId=null, supplierId=null) => {
    if (subcontractorId && supplierId) {
      const sub = subcontractors.find(sub => sub.subcontractor_id === subcontractorId);
      if (sub) {
        setSubcontractors(prevSubcontractors =>
          prevSubcontractors.map(sub =>
            sub.subcontractor_id === subcontractorId
              ? { ...sub, precoro_supplier_id: supplierId }
              : sub
          )
        );
      } else {
        await loadSubcontractors();
      }
      const supplier = suppliers.find(supplier => supplier.supplier_id === supplierId);
      if (supplier) {
        setSuppliers(prevSuppliers =>
          prevSuppliers.map(supplier =>
            supplier.supplier_id === supplierId
              ? { ...supplier, subcontractor_id: subcontractorId }
              : supplier
          )
        );
      } else {
        await loadSuppliers();
      }
    } else {
      await loadSubcontractors();
      await loadSuppliers();
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      align: 'center',
      width: '10%',
      sorter: (a, b, sortOrder) => {
        if (a.country === 'Unknown' && b.country === 'Unknown') return sortOrder === 'descend' ? 1 : 0;
        if (a.country === 'Unknown') return sortOrder === 'descend' ? -1 : 1;
        if (b.country === 'Unknown') return sortOrder === 'descend' ? 1 : -1;
        return a.country.localeCompare(b.country);
      },
      sortDirections: ['ascend', 'descend'],
      filters: [
        { text: 'Canada', value: 'Canada' },
        { text: 'USA', value: 'USA' },
        { text: 'Unknown', value: 'Unknown' },
      ],
      onFilter: (value, record) => record.country === value,
      render: (country) => {
        if (country === 'Canada') {
          return <CA title="Canada" style={{ width: '24px', height: '16px' }} />;
        } else if (country === 'USA') {
          return <US title="United States" style={{ width: '24px', height: '16px' }} />;
        } else {
          return <Tooltip title="Subcontractor matched to Precoro Supplier"><WarningOutlined style={{ color: 'orange', fontSize: '18px' }} /></Tooltip>
        }
      },
    },
    {
      title: 'Precoro',
      dataIndex: 'precoro_supplier_id',
      key: 'precoro_supplier_id',
      align: 'center',
      width: '10%',
      sorter: (a, b) => {
        if (a.precoro_supplier_id === null && b.precoro_supplier_id === null) return 0;
        if (a.precoro_supplier_id === null) return -1;
        if (b.precoro_supplier_id === null) return 1;
        return a.precoro_supplier_id - b.precoro_supplier_id;
      },
      sortDirections: ['ascend', 'descend'],
      filters: [
        { text: 'With Supplier ID', value: 'with' },
        { text: 'No Supplier ID', value: 'none' },
      ],
      onFilter: (value, record) => {
        if (value === 'with') {
          return record.precoro_supplier_id !== null;
        }
        return record.precoro_supplier_id === null;
      },
      render: (precoro_supplier_id) => {
        if (precoro_supplier_id === null) {
          return (
            <Tooltip title="Subcontractor is not matched to a Precoro Supplier">
              <WarningOutlined style={{ color: 'orange', fontSize: '18px' }} />
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title="Subcontractor matched to Precoro Supplier">
              <CheckOutlined style={{ color: 'green', fontSize: '18px' }} />
            </Tooltip>
          );
        }
      },
    },
    {
      title: 'Contact Name',
      dataIndex: 'contact_name',
      key: 'contact_name',
    },
    {
      title: 'Contact Email',
      dataIndex: 'contact_email',
      key: 'contact_email',
      width: '20%',
    },
    {
      title: 'Contact Number',
      dataIndex: 'contact_num',
      key: 'contact_num',
    },
    {
      title: '',
      key: 'actions',
      width: '5%',
      render: (subcontractor) => (
        <Button
          shape='circle'
          icon={<EditOutlined />}
          onClick={() => handleEditClickOpen(subcontractor)}
          style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none'}}
        />
      ),
    },
  ]

  const expandedColumns = [
    {
      title: 'Route Name',
      dataIndex: 'route_name',
      key: 'route_name',
    },
    {
      title: 'Contract Type',
      dataIndex: 'contract_type',
      key: 'contract_type',
    },
    {
      title: 'Salting Amount',
      dataIndex: 'salting_amount',
      key: 'salting_amount',
      render: (salting_amount, record) => {
        if (record.contract_type === constants.contract_type.all_inclusive) {
          return 'N/A';
        }
        return salting_amount != null ? `$${salting_amount.toFixed(2)}` : '-';
      },
    },
    {
      title: 'Saltings Performed',
      dataIndex: 'number_of_saltings_performed',
      key: 'number_of_saltings_performed',
      render: (text, record) => (
        record.contract_type === constants.contract_type.flat_rate ? text : 'N/A'
      ),
    },
    {
      title: 'Saltings Included',
      dataIndex: 'number_of_saltings_included',
      key: 'number_of_saltings_included',
      render: (text, record) => (
        record.contract_type === constants.contract_type.flat_rate ? text : 'N/A'
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      align: 'center',
      render: (routeRecord) => (
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          <Button
            shape='circle'
            icon={<EditOutlined />}
            onClick={() => handleEditRouteOpen(routeRecord.subcontractor_id, routeRecord)}
            style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none'}}
          />
          <Button
            shape='circle'
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveSubcontractorRoute(routeRecord.route_id, routeRecord.subcontractor_id)}
            style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none'}}
          />
        </div>
      ),
    },
  ];
    
  const [tableHeight, setTableHeight] = useState('100vh - 72px - 16px');
  const titleRef = useRef(null);
  const actionsRef = useRef(null);
  const tableRef = useRef(null);

  useLayoutEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleHeight = titleRef.current ? titleRef.current.offsetHeight : 48;
      const actionsHeight = actionsRef.current ? actionsRef.current.offsetHeight : 32;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 55;

      const totalHeightAroundTableRows =
        headerHeight +
        titleHeight +
        actionsHeight +
        padding * 4 +
        tableHeaderHeight;
      console.log(totalHeightAroundTableRows);
      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    calculateTableHeight();

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, []);

  if (constantsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingOutlined style={{ fontSize: '48px' }} spin />
      </div>
    );
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20', colorLinkHover: '#F57D20', colorLink: '#F57D20' } }}>
      {contextHolder}
      <div style={{ display: 'flex' }}>
        <SideMenu />
        
        <Card style={{ height: 'calc(100vh - 72px - 16px)', marginRight: '16px', overflow: 'hidden', divShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
          <Typography.Title ref={titleRef} level={2} style={{ margin: 0, paddingBottom: '10px' }}>
            Subcontractors
          </Typography.Title>
          
          <div ref={actionsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Input
              placeholder="Search Subcontractors"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: '300px' }}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                onClick={() => setSuppliersOpen(true)}
                disabled={loading}
              >
                Precoro Suppliers <ApiOutlined />
              </Button>

              <Tooltip title="Sync Subcontractors  with Yeti">
                <Button
                  shape="circle"
                  icon={<SyncOutlined />}
                  onClick={() => setSyncOpen(true)}
                  disabled={syncLoading}
                />
              </Tooltip>
            </div>
          </div>

          <div ref={tableRef} style={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              columns={columns}
              dataSource={filteredSubcontractors}
              loading={loading}
              pagination={false}
              scroll={{ y: tableHeight }}
              rowKey={(record) => record.subcontractor_id}
              expandable={{
                expandedRowRender: (record) => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        marginBottom: '10px',
                      }}
                    >
                      <Typography variant="h6">Route Assignments</Typography>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px', // Add some space between the select and the button
                          width: '50%',
                        }}
                      >
                        <Select
                          value={selectedRouteForAssignment}
                          onChange={(option) => setSelectedRouteForAssignment(option)}
                          options={
                            (record.availableRoutes || [])
                              .filter((route) =>
                                route.route_name.toLowerCase().includes(routeSearchQuery.toLowerCase())
                              )
                              .map((route) => ({
                                value: route.route_id,
                                label: route.route_name,
                              }))
                          }
                          placeholder="Select a route to assign"
                          style={{ width: '100%' }}
                          allowClear
                          showSearch
                          filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                          }
                        />
                        <Button
                          type="primary"
                          disabled={!selectedRouteForAssignment}
                          onClick={() =>
                            handleAddSubcontractorRoute(record.subcontractor_id, selectedRouteForAssignment)
                          }
                        >
                          Assign Route
                        </Button>
                      </div>
                    </div>
                    <Table
                      columns={expandedColumns}
                      dataSource={
                        record.routes
                          ? record.routes.map((route) => ({
                              ...route,
                              subcontractor_id: record.subcontractor_id,
                            }))
                          : []
                      }
                      rowKey="route_id"
                      pagination={false}
                      style={{ width: '100%' }}
                    />
                  </div>
                ),
                expandedRowKeys: [expandedSubcontractor],
                onExpand: (expanded, record) => {
                  handleExpandSubcontractor(record.subcontractor_id);
                },
              }}
            />
          </div>
        </Card>
      </div>

      <Modal
        width={600}
        open={editOpen}
        onCancel={handleEditClose}
        onOk={handleEditOk}
        title="Edit Subcontractor"
        centered
        footer={[
          <Button key="cancel" onClick={handleEditClose}>
            Cancel
          </Button>,
          <Button key="update" type="primary" onClick={handleEditOk}>
            Update
          </Button>,
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
          onValuesChange={(changedValues) => {
            if (changedValues.country) {
              setFilteredSuppliers(
                suppliers.filter(supplier => supplier.country === changedValues.country)
              );
              editForm.setFieldValue('precoro_supplier_id', null);
            }
          }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please input a name' }]}
          >
            <Input placeholder="Name" />
          </Form.Item>
    
          <Form.Item label="Country" name="country">
            <Select
              options={[
                { value: constants?.country.canada, label: constants?.country.canada },
                { value: constants?.country.usa, label: constants?.country.usa },
                { value: constants?.country.unknown, label: constants?.country.unknown },
              ]}
            />
          </Form.Item>
    
          <Form.Item label="Precoro Supplier" name="precoro_supplier_id">
            <Select
              placeholder="Select Precoro Supplier"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={filteredSuppliers.map(supplier => ({
                value: supplier.id,
                label: supplier.name,
              }))}
            />
          </Form.Item>
    
          <Form.Item
            label="Contact Name"
            name="contact_name"
            rules={[{ required: true, message: 'Please input a contact name' }]}
          >
            <Input placeholder="Contact Name" />
          </Form.Item>
    
          <Form.Item
            label="Contact Number"
            name="contact_num"
            rules={[{ required: true, message: 'Please enter a phone number' }]}
          >
            <Input placeholder="Contact Number" />
          </Form.Item>
    
          <Form.Item
            label="Contact Email"
            name="contact_email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Contact Email" />
          </Form.Item>
    
          <Form.Item
            label="Snow Contract Type"
            name="snow_contract_type"
            rules={[{ required: true, message: 'Please select a snow contract type' }]}
          >
            <Select
              placeholder="Snow Contract Type"
              options={[
                { value: constants.contract_type.hourly, label: constants.contract_type.hourly },
                { value: constants.contract_type.accum, label: constants.contract_type.accum },
                { value: constants.contract_type.per_push, label: constants.contract_type.per_push },
                { value: constants.contract_type.all_inclusive, label: constants.contract_type.all_inclusive }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    
      <Modal 
        open={editRouteOpen} 
        onClose={handleEditRouteClose} 
        onCancel={handleEditRouteClose} 
        onOk={handleEditRouteSave} 
        centered 
        title="Edit Route Subcontractor Assignment"
      >
        <Form layout='vertical'>
          <Form.Item label='Salting Contract Type' required>
            <Select
              name='contract_type'
              value={editingRoute?.contract_type}
              onChange={(value) => handleEditRouteChange({ target: { name: 'contract_type', value } })}
              style={{ width: '100%' }}
              placeholder='Select Contract Type'
            >
              <Select.Option value={constants?.contract_type.per_visit}>
                {constants?.contract_type.per_visit}
              </Select.Option>
              <Select.Option value={constants?.contract_type.flat_rate}>
                {constants?.contract_type.flat_rate}
              </Select.Option>
              <Select.Option value={constants?.contract_type.all_inclusive}>
                {constants?.contract_type.all_inclusive}
              </Select.Option>
            </Select>
          </Form.Item>

          {/* Only render Salting Amount if the contract type is not All-Inclusive */}
          {editingRoute?.contract_type !== constants?.contract_type.all_inclusive && (
            <Form.Item label="Salting Amount" required>
              <Input
                name="salting_amount"
                type="number"
                value={editingRoute?.salting_amount}
                onChange={handleEditRouteChange}
                prefix="$"
                placeholder="Enter Salting Amount"
              />
            </Form.Item>
          )}

          {/* Only render Saltings Included and Performed fields if the contract type is Flat Rate */}
          {editingRoute?.contract_type === constants?.contract_type.flat_rate && (
            <>
              <Form.Item label="Number of Saltings Performed" required>
                <InputNumber
                  name="number_of_saltings_performed"
                  value={editingRoute?.number_of_saltings_performed}
                  onChange={(value) =>
                    handleEditRouteChange({ target: { name: 'number_of_saltings_performed', value } })
                  }
                  style={{ width: '100%' }}
                  placeholder="Enter number"
                />
              </Form.Item>
      
              <Form.Item label="Number of Saltings Included" required>
                <InputNumber
                  name="number_of_saltings_included"
                  value={editingRoute?.number_of_saltings_included}
                  onChange={(value) =>
                    handleEditRouteChange({ target: { name: 'number_of_saltings_included', value } })
                  }
                  style={{ width: '100%' }}
                  placeholder="Enter number"
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={syncSummary ? "Yeti Subcontractor Sync Summary" : "Sync Subcontractors with Yeti"}
        open={syncOpen}
        onCancel={handleSyncClose}
        footer={[
          <Button key="close" onClick={handleSyncClose} disabled={syncLoading}>Close</Button>,
          !syncSummary && <Button key="start" type="primary" onClick={handleYetiSync} disabled={syncLoading}>Start Subcontractor Sync</Button>
        ]}
      >
        {syncLoading ? (
          <Space direction="horizontal" size="middle">
            <Typography variant='text'>Syncing Subcontractors</Typography>
            <LoadingOutlined style={{ fontSize: '32px' }} spin />
          </Space>
        ) : syncSummary ? (
          <div>
            <br/>
            {renderSubcontractorsSummary(syncSummary)}
          </div>
        ) : (
          <Typography variant='text'>Syncing Subcontractors with Yeti will add new Subcontractors and update existing Subcontractor records.</Typography>
        )}
      </Modal>

      <PrecoroSuppliers
        open={suppliersOpen}
        onClose={() => setSuppliersOpen(false)}
        suppliersData={suppliers}
        onSupplierUpdate={onSupplierUpdate}
        messageApi={messageApi}
      />
    </ConfigProvider>
  );
};

export default SubcontractorsPage;
